import React, { useEffect, useState } from "react";
import { getAuth, signOut } from "firebase/auth";
import { getFirestore, collection, query, where, getDocs, doc, getDoc, updateDoc, onSnapshot } from "firebase/firestore";
import app, { programHeadAuth } from "../../firebase";
import { useParams, useNavigate } from "react-router-dom";
import { isCalendarDateInRange, useCalendarClock } from "../../utils/announcementDates";

const auth = programHeadAuth;

function splitMeetingLines(value) {
  if (Array.isArray(value)) return value.map(item => String(item || '').trim()).filter(Boolean);
  if (typeof value !== 'string') return value ? [String(value)] : [];
  return value.split(/\r?\n/).map(line => line.trim()).filter(Boolean);
}

export default function StudentSchedule() {
  const navigate = useNavigate();
  const { studentId } = useParams();
  const [student, setStudent] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phName, setPhName] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [academicCalendar, setAcademicCalendar] = useState(null);
  const [studentScheduleFilterSchoolYear, setStudentScheduleFilterSchoolYear] = useState("");
  const [studentScheduleFilterSemester, setStudentScheduleFilterSemester] = useState("");
  const [studentScheduleFilterYearLevel, setStudentScheduleFilterYearLevel] = useState("");
  const [unitLimitError, setUnitLimitError] = useState("");
  const todayKey = useCalendarClock();

  const profilePicUrl = "https://ui-avatars.com/api/?name=" + (phName || "Program Head");

  // Logout handler
  const handleLogout = async () => {
    await signOut(auth);
    navigate("/academics");
  };

  // Handler to add selected schedules to student document
  const handleAddSchedule = async () => {
    if (!student || selectedRows.length === 0) return;
    const selectedCourses = new Map();
    filteredSchedules
      .filter(schedule => selectedRows.includes(schedule.id))
      .forEach(schedule => {
        const courseKey = [schedule.courseId, schedule.courseName, schedule.section].join('|');
        if (!selectedCourses.has(courseKey)) {
          selectedCourses.set(courseKey, parseFloat(schedule.units) || 0);
        }
      });
    const selectedUnitTotal = [...selectedCourses.values()].reduce((sum, units) => sum + units, 0);
    if (selectedUnitTotal > 30) {
      setUnitLimitError(
        `The selected courses total ${selectedUnitTotal.toFixed(1)} units. Students may add a maximum of 30 units. Remove some courses and try again.`
      );
      return;
    }
    const db = getFirestore(app);
    
    // Check if any selected schedules are full
    const selectedScheduleIds = selectedRows;
    const fullSchedules = [];
    
    for (const schedId of selectedScheduleIds) {
      const sched = filteredSchedules.find(s => s.id === schedId);
      if (sched) {
        const reserved = await getReservedCount(sched.courseId, sched.section);
        const limit = sched.limit || 40;
        if (reserved >= limit) {
          fullSchedules.push(`${sched.courseId} - Section ${sched.section}`);
        }
      }
    }
    
    if (fullSchedules.length > 0) {
      alert(`Cannot add schedules. The following are full:\n${fullSchedules.join('\n')}`);
      return;
    }
    
    // Get selected schedule objects
    const selectedSchedules = filteredSchedules.filter(s => selectedRows.includes(s.id)).map(s => ({
      schoolYear: s.schoolYear || '',
      semester: s.semester || '',
      yearLevel: s.yearLevel || '',
      section: s.section || '',
      courseId: s.courseId || '',
      courseName: s.courseName || '',
      units: s.units || '',
      type: s.type || '',
      day: s.day || '',
      time: s.time || '',
      room: s.room || '',
      prof: s.prof || '',
      limit: s.limit || 40
    }));
    
    // Build termKey from schedule data (schoolYear and semester)
    const termKey = `${selectedSchedules[0].schoolYear || "AY-Unknown"}_${selectedSchedules[0].semester || "Term-Unknown"}`;
    
    // Save to student document under termSchedules.{termKey}
    const studentRef = doc(db, 'students', student.id);
    await updateDoc(studentRef, {
      [`termSchedules.${termKey}`]: selectedSchedules
    });
    setStudent(prev => ({
      ...prev,
      termSchedules: {
        ...(prev.termSchedules || {}),
        [termKey]: selectedSchedules
      }
    }));
    setSelectedRows([]); // Clear selection after saving
    alert('Selected schedules added to student!');
  };

  // Filter states for dropdowns
  const [filterSchoolYear, setFilterSchoolYear] = useState("");
  const [filterSemester, setFilterSemester] = useState("");
  const [filterYearLevel, setFilterYearLevel] = useState("");
  const [filterSection, setFilterSection] = useState("");
  const [filterSubject, setFilterSubject] = useState("");
  const [scheduleTablePage, setScheduleTablePage] = useState(1);
  const scheduleRowsPerPage = 15;

  // State for selected rows in the schedule table
  const [selectedRows, setSelectedRows] = useState([]);
  const [reservedCounts, setReservedCounts] = useState({});
  const [enrolledCounts, setEnrolledCounts] = useState({});

  // Function to get reserved count for a course+section
  const getReservedCount = async (courseId, section) => {
    const db = getFirestore(app);
    const studentsRef = collection(db, 'students');
    const studentsSnap = await getDocs(studentsRef);
    
    let count = 0;
    studentsSnap.docs.forEach(doc => {
      const data = doc.data();
      const termSchedules = data.termSchedules || {};
      Object.values(termSchedules).forEach(schedArray => {
        if (Array.isArray(schedArray)) {
          const hasMatch = schedArray.some(s => s.courseId === courseId && s.section === section);
          if (hasMatch) count++;
        }
      });
    });
    return count;
  };

  // Auth state listener - maintain session and redirect if not authenticated
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate("/academics");
      }
    });
    return unsubscribe;
  }, [navigate]);

  // Fetch program head info
  useEffect(() => {
    const fetchProgramHeadInfo = async () => {
      const user = auth.currentUser;
      if (!user) {
        setPhName("");
        return;
      }
      const db = getFirestore(app);
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("email", "==", user.email));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const data = snap.docs[0].data();
        setPhName(data.name || "");
      }
    };
    fetchProgramHeadInfo();
  }, []);

  useEffect(() => onSnapshot(
    doc(getFirestore(app), "systemSettings", "announcement"),
    snapshot => setAcademicCalendar(snapshot.exists() ? snapshot.data() : null),
    error => {
      console.error("Failed to load active academic term for student schedules:", error);
      setAcademicCalendar(null);
    }
  ), []);

  useEffect(() => {
    const fetchStudent = async () => {
      setLoading(true);
      const db = getFirestore(app);
      const studentRef = doc(db, "students", studentId);
      const snap = await getDoc(studentRef);
      if (snap.exists()) {
        setStudent({ id: snap.id, ...snap.data() });
      }
      setLoading(false);
    };
    const fetchSchedules = async () => {
      setLoading(true);
      const db = getFirestore(app);
      // Only fetch schedules matching student's info from 'schedules' collection
      let filters = [];
      if (student) {
        filters = [
          where("department", "==", student.program),
          where("yearLevel", "==", student.preRegistration?.entryLevel || ""),
          where("semester", "==", student.preRegistration?.semester || ""),
        ];
        if (student.preRegistration?.section) {
          filters.push(where("section", "==", student.preRegistration.section));
        }
      }
      const q = filters.length > 0
        ? query(collection(db, "schedules"), ...filters)
        : query(collection(db, "schedules"));
      const snap = await getDocs(q);
      setSchedules(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    };
    if (studentId) {
      fetchStudent();
    }
  }, [studentId]);

  // Refetch schedules when student info is loaded
  useEffect(() => {
    if (student) {
      const fetchSchedules = async () => {
        setLoading(true);
        const db = getFirestore(app);
        // Fetch ALL schedules for the department to allow filtering
        const q = query(
          collection(db, "schedules"),
          where("department", "==", student.program)
        );
        const snap = await getDocs(q);
        setSchedules(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setLoading(false);
      };
      fetchSchedules();
    }
  }, [student]);

  // Calculate reserved counts when schedules change
  useEffect(() => {
    const calculateReservedCounts = async () => {
      if (schedules.length === 0) return;
      const counts = {};
      const uniqueCourses = Array.from(new Set(schedules.map(s => `${s.courseId}|${s.section}`)));
      
      for (const key of uniqueCourses) {
        const [courseId, section] = key.split('|');
        counts[key] = await getReservedCount(courseId, section);
      }
      setReservedCounts(counts);
    };
    calculateReservedCounts();
  }, [schedules]);

  useEffect(() => {
    if (!student?.program || schedules.length === 0) {
      setEnrolledCounts({});
      return;
    }

    const firestore = getFirestore(app);
    const unsubscribe = onSnapshot(collection(firestore, "students"), snapshot => {
      const counts = {};
      const scheduleGroups = new Map();
      schedules.forEach(schedule => {
        const key = `${schedule.courseId}|${schedule.section}`;
        if (!scheduleGroups.has(key)) scheduleGroups.set(key, []);
        scheduleGroups.get(key).push(schedule);
      });

      snapshot.docs.forEach(studentDoc => {
        const enrolledStudent = studentDoc.data();
        if (enrolledStudent.program !== student.program ||
          String(enrolledStudent.registrationStatus || "").toLowerCase() !== "enrolled") return;

        const assignments = Object.values(enrolledStudent.termSchedules || {}).flatMap(termSchedules =>
          Array.isArray(termSchedules) ? termSchedules : []
        );
        const countedGroups = new Set();
        assignments.forEach(assignment => {
          const key = `${assignment.courseId}|${assignment.section}`;
          if (countedGroups.has(key)) return;
          const matchingSchedules = scheduleGroups.get(key) || [];
          const matchesDepartmentSchedule = matchingSchedules.some(schedule =>
            (!assignment.schoolYear || assignment.schoolYear === schedule.schoolYear) &&
            (!assignment.semester || assignment.semester === schedule.semester) &&
            (!assignment.yearLevel || assignment.yearLevel === schedule.yearLevel)
          );
          if (matchesDepartmentSchedule) {
            counts[key] = (counts[key] || 0) + 1;
            countedGroups.add(key);
          }
        });
      });

      setEnrolledCounts(counts);
    }, error => {
      console.error("Error loading enrolled student counts:", error);
      setEnrolledCounts({});
    });

    return unsubscribe;
  }, [student?.program, schedules]);

  // Get unique values for dropdowns
  const schoolYearOptions = Array.from(new Set(schedules.map(s => s.schoolYear))).filter(Boolean);
  const semesterOptions = Array.from(new Set(schedules.map(s => s.semester))).filter(Boolean);
  const yearLevelOptions = Array.from(new Set(schedules.map(s => s.yearLevel))).filter(Boolean);
  const sectionOptions = Array.from(new Set(schedules.map(s => s.section))).filter(Boolean);

  // Filter schedules based on dropdowns
  const filteredSchedules = schedules.filter(s => {
    const schoolYearMatch = !filterSchoolYear || s.schoolYear === filterSchoolYear;
    const semesterMatch = !filterSemester || s.semester === filterSemester;
    const yearLevelMatch = !filterYearLevel || s.yearLevel === filterYearLevel;
    const sectionMatch = !filterSection || s.section === filterSection;
    const subjectSearch = filterSubject.trim().toLowerCase();
    const subjectMatch = !subjectSearch ||
      String(s.courseId || '').toLowerCase().includes(subjectSearch) ||
      String(s.courseName || '').toLowerCase().includes(subjectSearch);
    return schoolYearMatch && semesterMatch && yearLevelMatch && sectionMatch && subjectMatch;
  });
  const filteredGroupedSchedules = Object.values(
    filteredSchedules.reduce((acc, sched) => {
      const key = [sched.courseId, sched.courseName, sched.section].join('|');
      if (!acc[key]) {
        acc[key] = {
          ids: [],
          courseId: sched.courseId,
          courseName: sched.courseName,
          units: sched.units,
          section: sched.section,
          meetings: []
        };
      }
      acc[key].ids.push(sched.id);
      acc[key].meetings.push({
        id: sched.id,
        day: sched.day,
        time: sched.time,
        room: sched.room,
        prof: sched.prof
      });
      return acc;
    }, {})
  ).map(row => ({
    ...row,
    meetingEntries: row.meetings.flatMap(meeting => {
      const days = splitMeetingLines(meeting.day);
      const times = splitMeetingLines(meeting.time);
      const rooms = splitMeetingLines(meeting.room);
      const professors = splitMeetingLines(meeting.prof);
      const entryCount = Math.max(days.length, times.length, rooms.length, professors.length, 1);
      return Array.from({ length: entryCount }, (_, index) => ({
        day: days[index] || '-',
        time: times[index] || '-',
        room: rooms[index] || '-',
        prof: professors[index] || professors[0] || '-',
      }));
    })
  }));
  const scheduleTablePageCount = Math.ceil(filteredGroupedSchedules.length / scheduleRowsPerPage);
  const paginatedScheduleRows = filteredGroupedSchedules.slice(
    (scheduleTablePage - 1) * scheduleRowsPerPage,
    scheduleTablePage * scheduleRowsPerPage
  );
  const allFilteredScheduleIds = filteredGroupedSchedules.flatMap(row => row.ids);
  const totalFilteredUnits = filteredGroupedSchedules.reduce(
    (sum, row) => sum + (parseFloat(row.units) || 0),
    0
  );
  useEffect(() => {
    setScheduleTablePage(1);
  }, [filterSchoolYear, filterSemester, filterYearLevel, filterSection, filterSubject]);
  useEffect(() => {
    setScheduleTablePage(page => Math.min(page, Math.max(scheduleTablePageCount, 1)));
  }, [scheduleTablePageCount]);

  const studentScheduleGroups = Object.entries(student?.termSchedules || {})
    .flatMap(([termKey, termSchedules]) => (
      Array.isArray(termSchedules)
        ? termSchedules.map(schedule => ({
          ...schedule,
          schoolYear: schedule.schoolYear || '',
          semester: schedule.semester || '',
          yearLevel: schedule.yearLevel || '',
          termKey
        }))
        : []
    ))
    .concat(
      Object.keys(student?.termSchedules || {}).length === 0 && Array.isArray(student?.schedules)
        ? student.schedules.map(schedule => ({ ...schedule, termKey: '' }))
        : []
    )
    .reduce((groups, schedule) => {
      const schoolYear = schedule.schoolYear || 'School year not specified';
      const semester = schedule.semester || 'Semester not specified';
      const yearLevel = schedule.yearLevel || 'Year level not specified';
      const key = [schoolYear, semester, yearLevel].join('|');
      if (!groups[key]) {
        groups[key] = { key, schoolYear, semester, yearLevel, schedules: [] };
      }
      groups[key].schedules.push(schedule);
      return groups;
    }, {});
  const sortedStudentScheduleGroups = Object.values(studentScheduleGroups)
    .map(group => {
      const seenCourses = new Set();
      const totalUnits = group.schedules.reduce((sum, schedule) => {
        const courseKey = [schedule.courseId, schedule.courseName, schedule.section].join('|');
        if (seenCourses.has(courseKey)) return sum;
        seenCourses.add(courseKey);
        return sum + (parseFloat(schedule.units) || 0);
      }, 0);
      return { ...group, totalUnits };
    })
    .sort((a, b) =>
      a.schoolYear.localeCompare(b.schoolYear) ||
      a.semester.localeCompare(b.semester) ||
      a.yearLevel.localeCompare(b.yearLevel)
    );
  const activeStudentTerm = [
    {
      schoolYearStart: academicCalendar?.firstSemYearStart,
      schoolYearEnd: academicCalendar?.firstSemYearEnd,
      semester: "First Semester",
      start: academicCalendar?.firstSemStart,
      end: academicCalendar?.firstSemEnd
    },
    {
      schoolYearStart: academicCalendar?.secondSemYearStart,
      schoolYearEnd: academicCalendar?.secondSemYearEnd,
      semester: "Second Semester",
      start: academicCalendar?.secondSemStart,
      end: academicCalendar?.secondSemEnd
    }
  ].find(term => isCalendarDateInRange(term.start, term.end, todayKey));
  const activeSchoolYear = activeStudentTerm?.schoolYearStart && activeStudentTerm?.schoolYearEnd
    ? `${activeStudentTerm.schoolYearStart}-${activeStudentTerm.schoolYearEnd}`
    : "";
  const studentScheduleSchoolYearOptions = [...new Set([
    ...(activeSchoolYear ? [activeSchoolYear] : []),
    ...sortedStudentScheduleGroups.map(group => group.schoolYear)
  ])]
    .filter(Boolean)
    .sort((a, b) => a === activeSchoolYear ? -1 : b === activeSchoolYear ? 1 : a.localeCompare(b));
  const studentScheduleSemesterOptions = [...new Set([
    ...(activeStudentTerm?.semester ? [activeStudentTerm.semester] : []),
    ...sortedStudentScheduleGroups.map(group => group.semester)
  ])]
    .filter(Boolean)
    .sort((a, b) => a === activeStudentTerm?.semester ? -1 : b === activeStudentTerm?.semester ? 1 : a.localeCompare(b));
  const studentScheduleYearLevelOptions = [...new Set(sortedStudentScheduleGroups.map(group => group.yearLevel))]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
  const filteredStudentScheduleGroups = sortedStudentScheduleGroups.filter(group =>
    (!studentScheduleFilterSchoolYear || group.schoolYear === studentScheduleFilterSchoolYear) &&
    (!studentScheduleFilterSemester || group.semester === studentScheduleFilterSemester) &&
    (!studentScheduleFilterYearLevel || group.yearLevel === studentScheduleFilterYearLevel)
  ).sort((a, b) => {
    const aIsActive = a.schoolYear === activeSchoolYear && a.semester === activeStudentTerm?.semester;
    const bIsActive = b.schoolYear === activeSchoolYear && b.semester === activeStudentTerm?.semester;
    return Number(bIsActive) - Number(aIsActive) ||
      a.schoolYear.localeCompare(b.schoolYear) ||
      a.semester.localeCompare(b.semester) ||
      a.yearLevel.localeCompare(b.yearLevel);
  });
  const clearStudentScheduleFilters = () => {
    setStudentScheduleFilterSchoolYear("");
    setStudentScheduleFilterSemester("");
    setStudentScheduleFilterYearLevel("");
  };

  return (
    <>
    <div className="program-head-layout" style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", flexDirection: "row" }}>
      {/* Sidebar */}
      <div className="program-head-sidebar" style={{ width: 220, background: "#155c2b", color: "#fff", display: "flex", flexDirection: "column", alignItems: "stretch", padding: "32px 0", minHeight: "100vh", boxShadow: "2px 0 8px #eee" }}>
        <div style={{ fontWeight: 700, fontSize: 22, textAlign: "center", marginBottom: 40, letterSpacing: 2 }}>ENROLLEASE</div>
        
        <button onClick={() => navigate("/program-head")} style={{ background: "transparent", color: "#fff", border: "none", borderRadius: 6, padding: "14px 0", fontWeight: 600, fontSize: 17, cursor: "pointer", textAlign: "left", paddingLeft: 32, marginBottom: 8, marginRight: 12, transition: "background 0.2s" }}>Home</button>
        <button onClick={() => navigate("/program-head?page=curriculum")} style={{ background: "transparent", color: "#fff", border: "none", borderRadius: 6, padding: "14px 0", fontWeight: 600, fontSize: 17, cursor: "pointer", textAlign: "left", paddingLeft: 32, marginBottom: 8, marginRight: 12, transition: "background 0.2s" }}>Curriculum</button>
        <button onClick={() => navigate("/program-head?page=scheduling")} style={{ background: "transparent", color: "#fff", border: "none", borderRadius: 6, padding: "14px 0", fontWeight: 600, fontSize: 17, cursor: "pointer", textAlign: "left", paddingLeft: 32, marginBottom: 8, marginRight: 12, transition: "background 0.2s" }}>Scheduling</button>
        <button onClick={() => navigate("/program-head/student-course-lineup")} style={{ background: "#e67e22", color: "#fff", border: "none", borderRadius: 6, padding: "14px 0", fontWeight: 600, fontSize: 17, cursor: "pointer", textAlign: "left", paddingLeft: 32, marginBottom: 8, marginRight: 12, transition: "background 0.2s", boxShadow: "0 2px 8px #e67e22aa" }}>Course Line-up</button>
        <div style={{ flex: 1 }} />
      </div>

      {/* Main area */}
      <div className="program-head-main" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        {/* Header */}
        <div className="program-head-header" style={{ height: 80, background: "#fff", display: "flex", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", padding: "0 40px", position: "relative", zIndex: 10, justifyContent: "space-between" }}>
          <h1 style={{ margin: "0", fontSize: 24, color: "#155c2b", fontWeight: 600 }}>Student Schedule</h1>
          
          <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
            <span style={{ marginRight: 16, fontWeight: 600, fontSize: 18, color: "#155c2b" }}>{phName || "Program Head"}</span>
            <button onClick={() => setDropdownOpen(!dropdownOpen)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>
              <img src={profilePicUrl} alt="Profile" style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", border: "2px solid #e67e22" }} />
            </button>
            
            {dropdownOpen && (
              <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 8, background: "#fff", border: "1px solid #e0e0e0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 20, minWidth: 200, overflow: "hidden" }}>
                <div style={{ padding: "12px 16px", fontWeight: 600, color: "#155c2b", borderBottom: "1px solid #f0f0f0" }}>{phName || "Program Head"}</div>
                <button onClick={() => { setDropdownOpen(false); handleLogout(); }} style={{ width: "100%", padding: "12px 16px", background: "transparent", border: "none", color: "#e67e22", fontSize: 14, fontWeight: 600, cursor: "pointer", textAlign: "left", transition: "background 0.2s" }} onMouseEnter={e => e.target.style.background = "#fef5f0"} onMouseLeave={e => e.target.style.background = "transparent"}>Log out</button>
              </div>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="program-head-content" style={{ flex: 1, padding: 40, overflowY: "auto" }}>
          <div style={{ maxWidth: 1500, margin: "0 auto" }}>
            <div style={{ marginTop: 24 }}>
      {loading ? (
        <div style={{ color: "#888" }}>Loading student details...</div>
      ) : !student ? (
        <div style={{ color: "#888" }}>Student not found.</div>
      ) : (
        <>
          <div style={{ marginBottom: 32 }}>
            <div><b>Name:</b> {student.firstName} {student.lastName}</div>
            <div><b>Department:</b> {student.program}</div>
            <div><b>Entry Level:</b> {student.preRegistration?.entryLevel || '-'} </div>
            <div><b>Semester:</b> {student.preRegistration?.semester || '-'} </div>
          </div>
          {/* Filters */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'end', gap: 16, marginBottom: 32 }}>
            <div>
              <label htmlFor="student-schedule-subject-filter">Filter by Subject Code/Name:</label><br />
              <input
                id="student-schedule-subject-filter"
                type="search"
                value={filterSubject}
                onChange={event => setFilterSubject(event.target.value)}
                placeholder="Search code or subject"
                style={{ boxSizing: 'border-box', width: 220, padding: 8, borderRadius: 6, border: '1px solid #ccc' }}
              />
            </div>
            <div>
              <label>Filter by School Year:</label><br />
              <select value={filterSchoolYear} onChange={e => setFilterSchoolYear(e.target.value)} style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', minWidth: 180 }}>
                <option value="">All</option>
                {schoolYearOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div>
              <label>Filter by Semester:</label><br />
              <select value={filterSemester} onChange={e => setFilterSemester(e.target.value)} style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', minWidth: 180 }}>
                <option value="">All</option>
                {semesterOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div>
              <label>Filter by Year Level:</label><br />
              <select value={filterYearLevel} onChange={e => setFilterYearLevel(e.target.value)} style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', minWidth: 180 }}>
                <option value="">All</option>
                {yearLevelOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div>
              <label>Filter by Section:</label><br />
              <select value={filterSection} onChange={e => setFilterSection(e.target.value)} style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', minWidth: 180 }}>
                <option value="">All</option>
                {sectionOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <button
              type="button"
              onClick={() => {
                setFilterSchoolYear("");
                setFilterSemester("");
                setFilterYearLevel("");
                setFilterSection("");
                setFilterSubject("");
              }}
              style={{ alignSelf: 'end', padding: '9px 14px', borderRadius: 6, border: '1px solid #155c2b', background: '#fff', color: '#155c2b', fontWeight: 700, cursor: 'pointer' }}
            >
              Clear all filters
            </button>
          </div>
          {/* Schedule Table */}
          <h4 style={{ marginBottom: 16 }}>Add Student Course Line-up</h4>
          {filteredSchedules.length === 0 ? (
            <div style={{ color: '#888' }}>No schedules found for this selection.</div>
          ) : (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                <thead>
                  <tr style={{ background: '#155c2b', color: '#fff' }}>
                    <th style={{ border: '1px solid #ccc', padding: 6 }}>
                      <input type="checkbox" onChange={e => {
                        const checked = e.target.checked;
                        // Select all schedule ids for grouped rows
                        setSelectedRows(checked ? allFilteredScheduleIds : []);
                      }}
                      checked={allFilteredScheduleIds.length > 0 && allFilteredScheduleIds.every(id => selectedRows.includes(id))}
                      style={{ cursor: 'pointer' }}
                      />
                    </th>
                    <th style={{ border: '1px solid #ccc', padding: 8 }}>Subject Code</th>
                    <th style={{ border: '1px solid #ccc', padding: 8 }}>Subject Description</th>
                    <th style={{ border: '1px solid #ccc', padding: 8 }}>Day</th>
                    <th style={{ border: '1px solid #ccc', padding: 8 }}>Schedule</th>
                    <th style={{ border: '1px solid #ccc', padding: 8 }}>Room</th>
                    <th style={{ border: '1px solid #ccc', padding: 8 }}>Units</th>
                    <th style={{ border: '1px solid #ccc', padding: 8 }}>Limit</th>
                    <th style={{ border: '1px solid #ccc', padding: 8 }}>Reserved</th>
                    <th style={{ border: '1px solid #ccc', padding: 8 }}>Enrolled</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedScheduleRows.map(row => (
                    <tr key={`${row.courseId}-${row.section}`}>
                      <td style={{ border: '1px solid #ccc', padding: 8, verticalAlign: 'top' }}>
                        <input
                          type="checkbox"
                          checked={row.ids.every(id => selectedRows.includes(id)) && row.ids.length > 0}
                          onChange={e => {
                            const checked = e.target.checked;
                            setSelectedRows(prev => {
                              if (checked) return Array.from(new Set([...prev, ...row.ids]));
                              return prev.filter(id => !row.ids.includes(id));
                            });
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td style={{ border: '1px solid #ccc', padding: 8, verticalAlign: 'top' }}>{row.courseId || '-'}</td>
                      <td style={{ border: '1px solid #ccc', padding: 8, verticalAlign: 'top' }}>{row.courseName || '-'}</td>
                      <td style={{ border: '1px solid #ccc', padding: 8, verticalAlign: 'top' }}>
                        {row.meetingEntries.map((meeting, index) => <div key={index}>{meeting.day}</div>)}
                      </td>
                      <td style={{ border: '1px solid #ccc', padding: 8, verticalAlign: 'top' }}>
                        {row.meetingEntries.map((meeting, index) => <div key={index}>{meeting.time}</div>)}
                      </td>
                      <td style={{ border: '1px solid #ccc', padding: 8, verticalAlign: 'top' }}>
                        {row.meetingEntries.map((meeting, index) => <div key={index}>{meeting.room}</div>)}
                      </td>
                      <td style={{ border: '1px solid #ccc', padding: 8, verticalAlign: 'top', textAlign: 'right' }}>{row.units || '-'}</td>
                      <td style={{ border: '1px solid #ccc', padding: 8, verticalAlign: 'top', textAlign: 'center' }}>{row.limit || 40}</td>
                      <td style={{ border: '1px solid #ccc', padding: 8, verticalAlign: 'top', textAlign: 'center' }}>{reservedCounts[`${row.courseId}|${row.section}`] || 0}</td>
                      <td style={{ border: '1px solid #ccc', padding: 8, verticalAlign: 'top', textAlign: 'center' }}>{enrolledCounts[`${row.courseId}|${row.section}`] || 0}</td>
                    </tr>
                  ))}
                  {/* TOTAL UNITS row */}
                  <tr>
                    <td colSpan={9} style={{ border: '1px solid #ccc', padding: 8, fontWeight: 'bold', textAlign: 'right' }}>TOTAL UNITS</td>
                    <td style={{ border: '1px solid #ccc', padding: 8, fontWeight: 'bold', textAlign: 'right' }}>
                      {totalFilteredUnits.toFixed(1)}
                    </td>
                  </tr>
                </tbody>
              </table>
              {scheduleTablePageCount > 1 && (
                <nav aria-label="Add Student Course Line-up pagination" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginTop: 16 }}>
                  <span>
                    Showing {(scheduleTablePage - 1) * scheduleRowsPerPage + 1}–{Math.min(scheduleTablePage * scheduleRowsPerPage, filteredGroupedSchedules.length)} of {filteredGroupedSchedules.length} courses
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => setScheduleTablePage(page => Math.max(1, page - 1))}
                      disabled={scheduleTablePage === 1}
                      style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #155c2b', background: scheduleTablePage === 1 ? '#f1f3f2' : '#fff', color: '#155c2b', cursor: scheduleTablePage === 1 ? 'not-allowed' : 'pointer' }}
                    >
                      Previous
                    </button>
                    <span aria-live="polite">Page {scheduleTablePage} of {scheduleTablePageCount}</span>
                    <button
                      type="button"
                      onClick={() => setScheduleTablePage(page => Math.min(scheduleTablePageCount, page + 1))}
                      disabled={scheduleTablePage === scheduleTablePageCount}
                      style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #155c2b', background: scheduleTablePage === scheduleTablePageCount ? '#f1f3f2' : '#fff', color: '#155c2b', cursor: scheduleTablePage === scheduleTablePageCount ? 'not-allowed' : 'pointer' }}
                    >
                      Next
                    </button>
                  </div>
                </nav>
              )}
              <div style={{ marginTop: 24, textAlign: 'right' }}>
                <button
                  onClick={handleAddSchedule}
                  disabled={selectedRows.length === 0}
                  style={{
                    padding: '10px 28px',
                    borderRadius: 8,
                    background: selectedRows.length === 0 ? '#ccc' : '#155c2b',
                    color: '#fff',
                    border: 'none',
                    fontWeight: 600,
                    fontSize: 16,
                    cursor: selectedRows.length === 0 ? 'not-allowed' : 'pointer',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.07)'
                  }}
                >Add Schedule</button>
              </div>
            </>
          )}
          <section aria-labelledby="student-saved-schedule-heading" style={{ marginTop: 36 }}>
            <h4 id="student-saved-schedule-heading" style={{ marginBottom: 16 }}>Student Schedule</h4>
            {sortedStudentScheduleGroups.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'end', gap: 16, marginBottom: 18, padding: 16, border: '1px solid #d7dfd9', borderRadius: 8, background: '#f8faf8' }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span>Filter by School Year:</span>
                  <select value={studentScheduleFilterSchoolYear} onChange={event => setStudentScheduleFilterSchoolYear(event.target.value)} style={{ minWidth: 190, padding: 8, border: '1px solid #b9c7bd', borderRadius: 6 }}>
                    <option value="">All School Years</option>
                    {studentScheduleSchoolYearOptions.map(year => (
                      <option key={year} value={year}>{year}{year === activeSchoolYear ? ' (Open)' : ''}</option>
                    ))}
                  </select>
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span>Filter by Semester:</span>
                  <select value={studentScheduleFilterSemester} onChange={event => setStudentScheduleFilterSemester(event.target.value)} style={{ minWidth: 190, padding: 8, border: '1px solid #b9c7bd', borderRadius: 6 }}>
                    <option value="">All Semesters</option>
                    {studentScheduleSemesterOptions.map(semester => (
                      <option key={semester} value={semester}>{semester}{semester === activeStudentTerm?.semester ? ' (Open)' : ''}</option>
                    ))}
                  </select>
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span>Filter by Year Level:</span>
                  <select value={studentScheduleFilterYearLevel} onChange={event => setStudentScheduleFilterYearLevel(event.target.value)} style={{ minWidth: 190, padding: 8, border: '1px solid #b9c7bd', borderRadius: 6 }}>
                    <option value="">All Year Levels</option>
                    {studentScheduleYearLevelOptions.map(level => <option key={level} value={level}>{level}</option>)}
                  </select>
                </label>
                <button type="button" onClick={clearStudentScheduleFilters} style={{ minHeight: 38, padding: '0 14px', border: '1px solid #155c2b', borderRadius: 6, background: '#fff', color: '#155c2b', fontWeight: 700, cursor: 'pointer' }}>
                  Clear all filters
                </button>
              </div>
            )}
            {sortedStudentScheduleGroups.length === 0 ? (
              <div style={{ color: '#888' }}>No schedules have been added to this student yet.</div>
            ) : filteredStudentScheduleGroups.length === 0 ? (
              <div style={{ color: '#888' }}>No student schedules match the selected filters.</div>
            ) : (
              <div style={{ display: 'grid', gap: 24 }}>
                {filteredStudentScheduleGroups.map(group => (
                  <section key={group.key} aria-label={`Student schedule for ${group.schoolYear}, ${group.semester}, ${group.yearLevel}`}>
                    <h5 style={{ margin: '0 0 10px', padding: '10px 12px', borderLeft: '4px solid #155c2b', borderRadius: 6, background: '#edf5ef', color: '#155c2b', fontSize: 16 }}>
                      School Year: {group.schoolYear} <span aria-hidden="true">·</span> Semester: {group.semester} <span aria-hidden="true">·</span> Year Level: {group.yearLevel}
                    </h5>
                    <div style={{ width: '100%', overflowX: 'auto', background: '#fff', border: '1px solid #d7dfd9', borderRadius: 8 }}>
                      <table style={{ width: '100%', minWidth: 900, borderCollapse: 'collapse', background: '#fff' }}>
                        <thead>
                          <tr style={{ background: '#155c2b', color: '#fff' }}>
                            {['Subject Code', 'Subject Description', 'Day', 'Schedule', 'Room', 'Section', 'Professor', 'Units'].map(heading => (
                              <th key={heading} scope="col" style={{ border: '1px solid #cbd5ce', padding: 9, textAlign: 'left', whiteSpace: 'nowrap' }}>{heading}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                        {group.schedules.map((schedule, index) => (
                          <tr key={`${group.key}-${schedule.courseId || 'course'}-${schedule.section || 'section'}-${index}`}>
                            <td style={{ border: '1px solid #d7dfd9', padding: 9 }}>{schedule.courseId || '-'}</td>
                            <td style={{ border: '1px solid #d7dfd9', padding: 9 }}>{schedule.courseName || '-'}</td>
                            <td style={{ border: '1px solid #d7dfd9', padding: 9, whiteSpace: 'pre-line' }}>{schedule.day || '-'}</td>
                            <td style={{ border: '1px solid #d7dfd9', padding: 9, whiteSpace: 'pre-line' }}>{schedule.time || '-'}</td>
                            <td style={{ border: '1px solid #d7dfd9', padding: 9, whiteSpace: 'pre-line' }}>{schedule.room || '-'}</td>
                            <td style={{ border: '1px solid #d7dfd9', padding: 9 }}>{schedule.section || '-'}</td>
                            <td style={{ border: '1px solid #d7dfd9', padding: 9 }}>{schedule.prof || schedule.professor || '-'}</td>
                            <td style={{ border: '1px solid #d7dfd9', padding: 9, textAlign: 'right' }}>{schedule.units || '-'}</td>
                          </tr>
                        ))}
                        <tr>
                          <td colSpan={7} style={{ border: '1px solid #cbd5ce', padding: 9, fontWeight: 700, textAlign: 'right', background: '#f3f7f4' }}>
                            TOTAL UNITS
                          </td>
                          <td style={{ border: '1px solid #cbd5ce', padding: 9, fontWeight: 700, textAlign: 'right', background: '#f3f7f4' }}>
                            {group.totalUnits.toFixed(1)}
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </section>
                ))}
              </div>
            )}
          </section>
        </>
      )}
            </div>
          </div>
        </div>
      </div>
    </div>
    {unitLimitError && (
      <div
        role="presentation"
        onMouseDown={event => {
          if (event.target === event.currentTarget) setUnitLimitError("");
        }}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 3000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
          background: "rgba(15, 23, 42, 0.55)"
        }}
      >
        <section
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="unit-limit-title"
          aria-describedby="unit-limit-message"
          style={{
            width: "100%",
            maxWidth: 460,
            padding: 26,
            borderRadius: 12,
            background: "#fff",
            boxShadow: "0 18px 48px rgba(0,0,0,0.24)"
          }}
        >
          <h2 id="unit-limit-title" style={{ margin: "0 0 12px", color: "#8a1c1c", fontSize: 21 }}>
            Unit Limit Exceeded
          </h2>
          <p id="unit-limit-message" style={{ margin: "0 0 20px", color: "#334155", lineHeight: 1.55 }}>
            {unitLimitError}
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={() => setUnitLimitError("")}
              autoFocus
              style={{ padding: "9px 18px", border: "none", borderRadius: 8, background: "#155c2b", color: "#fff", fontWeight: 700, cursor: "pointer" }}
            >
              Close
            </button>
          </div>
        </section>
      </div>
    )}
    </>
  );
}
